import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CATALOGO_UNAS_LOCAL, catalogoUnasPdfCandidates } from '../config/unasCatalog';

const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

let pdfjsPromise = null;

function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_CDN;
    script.async = true;
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error('PDF.js no cargó'));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('No se pudo cargar PDF.js'));
    document.head.appendChild(script);
  });
  return pdfjsPromise;
}

export default function CatalogoColores() {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const thumbsStripRef = useRef(null);
  const thumbRefs = useRef({});
  const docRef = useRef(null);
  const thumbsLoadedRef = useRef({});
  const renderGenRef = useRef(0);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState('');
  const [thumbUrls, setThumbUrls] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    async function openPdf(pdfjs) {
      const candidates = catalogoUnasPdfCandidates();
      let lastErr = null;
      for (const url of candidates) {
        try {
          const doc = await pdfjs.getDocument({ url, withCredentials: false }).promise;
          return { doc, url };
        } catch (err) {
          lastErr = err;
        }
      }
      throw lastErr || new Error('No se pudo abrir el catálogo');
    }

    loadPdfJs()
      .then((pdfjs) => openPdf(pdfjs))
      .then(({ doc }) => {
        if (cancelled) {
          doc.destroy();
          return;
        }
        docRef.current = doc;
        setNumPages(doc.numPages);
        setPage(1);
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'No se pudo abrir el catálogo');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      if (docRef.current) {
        docRef.current.destroy();
        docRef.current = null;
      }
    };
  }, []);

  const renderPage = useCallback(async (pageNum, { forThumb = false } = {}) => {
    const doc = docRef.current;
    if (!doc) return null;
    const pdfPage = await doc.getPage(pageNum);
    const base = pdfPage.getViewport({ scale: 1 });

    let scale;
    if (forThumb) {
      scale = 96 / base.width;
    } else {
      const frame = frameRef.current;
      const maxW = Math.max(240, (frame?.clientWidth || Math.min(720, window.innerWidth - 96)) - 8);
      const maxH = Math.max(280, (frame?.clientHeight || Math.min(window.innerHeight * 0.68, 720)) - 8);
      scale = Math.min(maxW / base.width, maxH / base.height);
    }

    const viewport = pdfPage.getViewport({ scale });
    const canvas = forThumb ? document.createElement('canvas') : canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d', { alpha: false });
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    await pdfPage.render({ canvasContext: ctx, viewport }).promise;
    if (forThumb) return canvas.toDataURL('image/jpeg', 0.72);
    return null;
  }, []);

  useEffect(() => {
    if (!numPages || loading || error) return;
    let cancelled = false;
    const gen = ++renderGenRef.current;
    setRendering(true);

    // Mantener el color arriba: no bajar la ventana al cambiar de página
    if (frameRef.current) frameRef.current.scrollTop = 0;
    if (stageRef.current) {
      const rect = stageRef.current.getBoundingClientRect();
      if (rect.top < 72 || rect.top > window.innerHeight * 0.35) {
        const y = window.scrollY + rect.top - 80;
        window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
      }
    }

    renderPage(page)
      .then(() => {
        if (cancelled || gen !== renderGenRef.current) return;
        if (frameRef.current) frameRef.current.scrollTop = 0;
      })
      .catch(() => {
        if (!cancelled && gen === renderGenRef.current) {
          setError('No se pudo mostrar esta página');
        }
      })
      .finally(() => {
        if (!cancelled && gen === renderGenRef.current) setRendering(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, numPages, loading, error, renderPage]);

  useEffect(() => {
    if (!numPages || !docRef.current) return;
    let cancelled = false;
    const start = Math.max(1, page - 4);
    const end = Math.min(numPages, page + 8);
    (async () => {
      for (let i = start; i <= end; i += 1) {
        if (cancelled || thumbsLoadedRef.current[i]) continue;
        thumbsLoadedRef.current[i] = true;
        try {
          const url = await renderPage(i, { forThumb: true });
          if (cancelled || !url) {
            thumbsLoadedRef.current[i] = false;
            continue;
          }
          setThumbUrls((prev) => ({ ...prev, [i]: url }));
        } catch {
          thumbsLoadedRef.current[i] = false;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, numPages, renderPage]);

  // Solo desplaza la franja de miniaturas (nunca la página)
  useEffect(() => {
    const strip = thumbsStripRef.current;
    const el = thumbRefs.current[page];
    if (!strip || !el) return;
    const left = el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [page]);

  const go = useCallback(
    (next) => {
      setPage((p) => Math.min(numPages || p, Math.max(1, next)));
    },
    [numPages]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') go(page - 1);
      if (e.key === 'ArrowRight') go(page + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, page]);

  return (
    <section className="unas-colores section">
      <header className="unas-colores-head">
        <p className="section-kicker">Catálogo oficial</p>
        <h2>Colores</h2>
      </header>

      {loading ? <div className="unas-colores-status">Cargando catálogo visual…</div> : null}

      {error ? (
        <div className="unas-colores-status error">
          <p>{error}</p>
          <div className="unas-pdf-actions">
            <a className="btn-primary" href={CATALOGO_UNAS_LOCAL} target="_blank" rel="noreferrer">
              Abrir PDF
            </a>
          </div>
        </div>
      ) : null}

      {!loading && !error && numPages > 0 ? (
        <>
          <div className="unas-colores-stage" ref={stageRef}>
            <button
              type="button"
              className="unas-colores-nav prev"
              aria-label="Página anterior"
              disabled={page <= 1}
              onClick={() => go(page - 1)}
            >
              ‹
            </button>
            <div
              ref={frameRef}
              className={`unas-colores-frame ${rendering ? 'is-rendering' : ''}`}
            >
              <canvas ref={canvasRef} />
            </div>
            <button
              type="button"
              className="unas-colores-nav next"
              aria-label="Página siguiente"
              disabled={page >= numPages}
              onClick={() => go(page + 1)}
            >
              ›
            </button>
          </div>

          <div className="unas-colores-meta">
            <strong>
              Página {page} de {numPages}
            </strong>
            <div className="unas-colores-jump">
              <button type="button" className="btn-ghost" disabled={page <= 1} onClick={() => go(1)}>
                Inicio
              </button>
              <button
                type="button"
                className="btn-ghost"
                disabled={page <= 1}
                onClick={() => go(page - 1)}
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn-ghost"
                disabled={page >= numPages}
                onClick={() => go(page + 1)}
              >
                Siguiente
              </button>
              <button
                type="button"
                className="btn-ghost"
                disabled={page >= numPages}
                onClick={() => go(numPages)}
              >
                Final
              </button>
            </div>
            <div className="unas-pdf-actions">
              <a className="btn-ghost" href={CATALOGO_UNAS_LOCAL} target="_blank" rel="noreferrer">
                Abrir PDF completo
              </a>
            </div>
          </div>

          <div
            className="unas-colores-thumbs"
            ref={thumbsStripRef}
            role="listbox"
            aria-label="Páginas del catálogo"
          >
            {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                ref={(el) => {
                  thumbRefs.current[n] = el;
                }}
                role="option"
                aria-selected={n === page}
                className={`unas-colores-thumb ${n === page ? 'active' : ''}`}
                onClick={() => go(n)}
              >
                {thumbUrls[n] ? (
                  <img src={thumbUrls[n]} alt={`Página ${n}`} loading="lazy" />
                ) : (
                  <span>{n}</span>
                )}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
